import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { errorMessage } from '../../services/apiError';
import {
  projectService,
  ProjectAgentPreference,
} from '../../services/projectService';
import {
  useLazyGetTaskQuery,
  useUpdateTaskMutation,
} from '../../store/services/tasksApi';
import { SearchIcon } from '../icons/AgentIcons';
import { ModalBackdrop } from '../ModalBackdrop';
import { ModalContent } from '../ModalContent';
import { useToast } from '../Toast/Toast';

import { AgentGroupSection } from './AgentGroupSection';
import {
  AgentPreference,
  AGENT_GROUPS,
  LOCKED_AGENT_IDS,
  FIXED_MODEL_OPTIONS,
} from './agentsModalTypes';
import {
  createInitialState,
  buildProjectAgentOverrides,
  getCachedProjectAgents,
  setCachedProjectAgents,
  applyGlobalTaskConstraints,
  buildTaskAgentOverrides,
  countEnabledAgents,
  countRequiredAgents,
} from './agentsModalUtils';
import { ModelTier } from './ChatBoxInput';

import './AgentsModal.css';

interface AgentsModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId?: string | null;
  taskId?: string | null;
  initialAgents?: Record<string, Partial<AgentPreference>>;
  onAgentsChange?: (agents: Record<string, AgentPreference>) => void;
  isTaskContext?: boolean;
  taskRole?: string;
}

export const AgentsModal: React.FC<AgentsModalProps> = ({
  isOpen,
  onClose,
  projectId,
  taskId,
  initialAgents,
  onAgentsChange,
  isTaskContext = false,
  taskRole,
}) => {
  const toast = useToast();
  const [getTask] = useLazyGetTaskQuery();
  const [updateTask] = useUpdateTaskMutation();
  const normalizedTaskId = taskId ?? null;
  const [search, setSearch] = useState('');
  const [openGroupId, setOpenGroupId] = useState<string>('execution');
  const [enabledAgents, setEnabledAgents] = useState<Record<string, AgentPreference>>(() => createInitialState(initialAgents));
  const [savedAgents, setSavedAgents] = useState<Record<string, AgentPreference>>(() => createInitialState(initialAgents));
  const [projectAgents, setProjectAgents] = useState<Record<string, AgentPreference>>(() => (
    getCachedProjectAgents(projectId)?.agents ?? createInitialState()
  ));
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [loadedProjectId, setLoadedProjectId] = useState<string | null>(() => (
    getCachedProjectAgents(projectId) ? projectId ?? null : null
  ));
  const [loadedTaskId, setLoadedTaskId] = useState<string | null>(null);
  const [loadedProjectCacheVersion, setLoadedProjectCacheVersion] = useState<number>(() => (
    getCachedProjectAgents(projectId)?.version ?? 0
  ));

  useEffect(() => {
    if (!projectId) {
      setLoadedProjectId(null);
      setLoadedProjectCacheVersion(0);
      setProjectAgents(createInitialState());
      return;
    }

    if (!isOpen) {
      // When modal is closed, stay in sync with global project settings from cache if they exist
      const cached = getCachedProjectAgents(projectId);
      const nextState = isTaskContext
        ? applyGlobalTaskConstraints(createInitialState(initialAgents), cached?.agents ?? createInitialState())
        : (cached?.agents ?? createInitialState(initialAgents));

      setProjectAgents(cached?.agents ?? createInitialState());
      setEnabledAgents(nextState);
      setSavedAgents(nextState);
      setLoadedProjectId(cached && projectId ? projectId : null);
      setLoadedTaskId(normalizedTaskId);
      setLoadedProjectCacheVersion(cached?.version ?? 0);
      return;
    }

    // When modal is open, attempt to sync from cache if we have a mismatch
    const cached = getCachedProjectAgents(projectId);
    if (cached && (loadedProjectId !== projectId || loadedProjectCacheVersion !== cached.version)) {
      setProjectAgents(cached.agents);
      const nextState = isTaskContext
        ? applyGlobalTaskConstraints(createInitialState(initialAgents), cached.agents)
        : cached.agents;
      setEnabledAgents(nextState);
      setSavedAgents(nextState);
      setLoadedProjectId(projectId);
      setLoadedTaskId(normalizedTaskId);
      setLoadedProjectCacheVersion(cached.version);
    }
  }, [initialAgents, isOpen, isTaskContext, projectId, loadedProjectId, loadedProjectCacheVersion, normalizedTaskId]);

  useEffect(() => {
    if (!isOpen || !projectId) return undefined;
    const cached = getCachedProjectAgents(projectId);

    // Use cache and passed-in props on open; only fetch if we have never loaded this project context.
    if (cached || (loadedProjectId === projectId && loadedTaskId === normalizedTaskId)) return undefined;

    let cancelled = false;
    const loadAgents = async () => {
      setIsLoading(true);
      try {
        const [projectResponse, taskResponse] = await Promise.all([
          projectService.getProjectAgents(projectId),
          (isTaskContext && taskId)
            ? getTask({ projectId, taskId }, true).unwrap()
            : Promise.resolve(null),
        ]);

        if (cancelled) return;

        const globalState = createInitialState(projectResponse.agents);
        setCachedProjectAgents(projectId, globalState);
        const cacheEntry = getCachedProjectAgents(projectId);

        setProjectAgents(globalState);
        
        // Use task response agents as the primary source of truth if available
        const taskPrefSource = taskResponse?.agents ?? initialAgents;
        const nextState = isTaskContext
          ? applyGlobalTaskConstraints(createInitialState(taskPrefSource), globalState)
          : globalState;

        setEnabledAgents(nextState);
        setSavedAgents(nextState);
        setLoadedProjectId(projectId);
        setLoadedTaskId(normalizedTaskId);
        setLoadedProjectCacheVersion(cacheEntry?.version ?? 0);
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to load project agents:', error);
          toast.error(errorMessage(error, 'We couldn’t load the agent preferences. Close and reopen this window.'));
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void loadAgents();
    return () => {
      cancelled = true;
    };
  }, [
    getTask,
    initialAgents,
    isOpen,
    isTaskContext,
    loadedProjectId,
    loadedTaskId,
    normalizedTaskId,
    projectId,
    taskId,
    toast,
  ]);

  const filteredGroups = useMemo(() => {
    const query = search.trim().toLowerCase();

    // Filter out Test Agent for UI/UX Designer and Frontend Developer roles
    const shouldHideTestAgent = isTaskContext 
      && taskRole 
      && (taskRole === 'UI/UX Designer' || taskRole === 'Frontend Developer');

    let groups = AGENT_GROUPS;

    if (shouldHideTestAgent) {
      groups = AGENT_GROUPS.map((group) => ({
        ...group,
        agents: group.agents.filter((agent) => agent.id !== 'test_agent'),
      }));
    }

    if (!query) return groups;

    return groups.map((group) => ({
      ...group,
      agents: group.agents.filter((agent) => (
        agent.label.toLowerCase().includes(query)
        || agent.description.toLowerCase().includes(query)
      )),
    })).filter((group) => group.agents.length > 0);
  }, [search, isTaskContext, taskRole]);

  const totals = useMemo(() => {
    const allAgents = AGENT_GROUPS.flatMap((group) => group.agents);
    const enabledCount = countEnabledAgents(allAgents, enabledAgents);
    const requiredCount = countRequiredAgents(allAgents);

    return {
      total: allAgents.length,
      enabled: enabledCount,
      disabled: allAgents.length - enabledCount,
      required: requiredCount,
      configurable: allAgents.length - requiredCount,
    };
  }, [enabledAgents]);

  const hasUnsavedChanges = useMemo(() => (
    Object.keys(enabledAgents).some((agentId) => (
      enabledAgents[agentId]?.enabled !== savedAgents[agentId]?.enabled
      || (!isTaskContext && enabledAgents[agentId]?.model_tier !== savedAgents[agentId]?.model_tier)
    ))
  ), [enabledAgents, savedAgents, isTaskContext]);

  const handleCancel = useCallback(() => {
    setEnabledAgents(savedAgents);
    onClose();
  }, [onClose, savedAgents]);

  const toggleAgent = useCallback((agentId: string) => {
    if (LOCKED_AGENT_IDS.has(agentId)) return;
    if (isTaskContext && projectAgents[agentId]?.enabled === false) return;

    setEnabledAgents((prev) => ({
      ...prev,
      [agentId]: {
        ...prev[agentId],
        enabled: !prev[agentId].enabled,
      },
    }));
  }, [isTaskContext, projectAgents]);

  const handleModelChange = useCallback((agentId: string, modelTier: ModelTier) => {
    const forcedModelTier = FIXED_MODEL_OPTIONS[agentId];
    setEnabledAgents((prev) => ({
      ...prev,
      [agentId]: {
        ...prev[agentId],
        model_tier: forcedModelTier ?? modelTier,
      },
    }));
  }, []);

  const toggleGroupCollapse = useCallback((groupId: string) => {
    setOpenGroupId((prev) => (prev === groupId ? '' : groupId));
  }, []);

  const setGroupSelection = useCallback((groupId: string, value: boolean) => {
    const group = AGENT_GROUPS.find((item) => item.id === groupId);

    if (!group) return;

    setEnabledAgents((prev) => {
      const next = { ...prev };

      group.agents.forEach((agent) => {
        if (LOCKED_AGENT_IDS.has(agent.id)) {
          next[agent.id] = {
            ...next[agent.id],
            enabled: true,
          };
          return;
        }
        // Don't allow enabling agents that are globally disabled
        if (isTaskContext && projectAgents[agent.id]?.enabled === false) {
          return;
        }
        next[agent.id] = {
          ...next[agent.id],
          enabled: value,
        };
      });

      return next;
    });
  }, [isTaskContext, projectAgents]);

  const saveTaskAgents = useCallback(async () => {
    if (!projectId || !taskId) return createInitialState();
    const agents = buildTaskAgentOverrides(
      enabledAgents,
      projectAgents,
    ) as Record<string, ProjectAgentPreference>;
    const updated = await updateTask({
      projectId,
      taskId,
      task: { agents },
    }).unwrap();

    return createInitialState(updated.agents);
  }, [enabledAgents, projectAgents, projectId, taskId, updateTask]);

  const handleSave = useCallback(async () => {
    if (!projectId || isSaving || isLoading || !hasUnsavedChanges || (isTaskContext && !taskId)) return;

    setIsSaving(true);
    try {
      const nextState = isTaskContext
        ? await saveTaskAgents()
        : createInitialState((await projectService.updateProjectAgents(
          projectId,
          buildProjectAgentOverrides(enabledAgents),
        )).agents);

      if (!isTaskContext) {
        setCachedProjectAgents(projectId, nextState);
        setProjectAgents(nextState);
      }

      setEnabledAgents(nextState);
      setSavedAgents(nextState);
      setLoadedProjectId(projectId);
      setLoadedTaskId(normalizedTaskId);
      onAgentsChange?.(nextState);
      toast.success(isTaskContext ? 'Task agents saved' : 'Agent preferences saved');
      
      if (!isTaskContext) {
        window.dispatchEvent(new Event('refresh_tasks'));
      }

      // Dispatch event to notify other components that agents have been updated
      window.dispatchEvent(new globalThis.CustomEvent('agents_updated', {
        detail: { projectId, taskId, isTaskContext } 
      }));
      
      onClose();
    } catch (error) {
      console.error('Failed to save agents:', error);
      toast.error(errorMessage(
        error,
        isTaskContext
          ? 'We couldn’t save the task agents. Review the selection and try again.'
          : 'We couldn’t save the agent preferences. Review the selection and try again.',
      ));
    } finally {
      setIsSaving(false);
    }
  }, [
    enabledAgents,
    hasUnsavedChanges,
    isLoading,
    isSaving,
    isTaskContext,
    normalizedTaskId,
    onAgentsChange,
    onClose,
    projectId,
    saveTaskAgents,
    taskId,
    toast,
  ]);

  if (!isOpen) return null;

  return (
    <ModalBackdrop isVisible={isOpen} onClick={handleCancel}>
      <ModalContent onClose={handleCancel} className="auth-modal-content agents-modal">
        <div className="auth-modal-header">
          <h2 className="auth-modal-title">
            {isTaskContext ? 'Task Agent Assignment' : 'Global Agent Settings'}
          </h2>
          <p className="auth-modal-subtitle">
            {isTaskContext
              ? 'Select which execution agents should run for this specific task.'
              : 'Choose which execution agents run across all tasks in this project, and set the model tier for each one.'}
          </p>
        </div>

        <div className="agents-modal__body">
          <div className="agents-modal__topbar">
            <div className="agents-modal__search">
              <SearchIcon size={16} className="agents-modal__search-icon" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search agents"
                className="agents-modal__search-field"
              />
            </div>

            <div className="agents-modal__summary">
              <span className="agents-modal__summary-chip">{totals.enabled} enabled</span>
              <span className="agents-modal__summary-chip agents-modal__summary-chip--muted">
                {totals.disabled} disabled
              </span>
              <span className="agents-modal__summary-chip agents-modal__summary-chip--muted">
                {totals.required} required
              </span>
              {!isTaskContext && (
                <span className="agents-modal__summary-chip agents-modal__summary-chip--accent">
                  Per-agent model tiers
                </span>
              )}
            </div>
          </div>

          {!isTaskContext && (
            <div className="agents-modal__legend">
              <span className="agents-modal__legend-title">How to use this</span>
              <span className="agents-modal__legend-copy">
                Toggle agents on or off globally, then pick a model tier for each row to balance speed, cost, and depth.
              </span>
            </div>
          )}

          {filteredGroups.length > 0 ? (
            filteredGroups.map((group) => (
              <AgentGroupSection
                key={group.id}
                group={group}
                collapsed={openGroupId !== group.id}
                enabledMap={enabledAgents}
                onToggleCollapse={toggleGroupCollapse}
                onToggleAgent={toggleAgent}
                onSelectAll={setGroupSelection}
                onModelChange={handleModelChange}
                showModelDropdown={!isTaskContext}
                projectAgents={projectAgents}
                isTaskContext={isTaskContext}
              />
            ))
          ) : (
            <div className="agents-modal__empty">
              <h3>No agents match that search</h3>
              <p>Try searching by agent name, role, or verification step.</p>
            </div>
          )}
        </div>

        <div className="agents-modal__footer">
          <span className="agents-modal__footer-copy">
            {isLoading
              ? 'Loading saved preferences...'
              : hasUnsavedChanges
                ? 'You have unsaved changes.'
                : 'All changes saved.'}
          </span>
          <div className="agents-modal__footer-actions">
            <button
              type="button"
              className="agents-modal__footer-btn agents-modal__footer-btn--ghost"
              onClick={handleCancel}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="agents-modal__footer-btn agents-modal__footer-btn--primary"
              onClick={() => void handleSave()}
              disabled={!projectId || isLoading || isSaving || !hasUnsavedChanges}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </ModalContent>
    </ModalBackdrop>
  );
};
